import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabaseClient'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

const AdminDashboard = () => {
  const navigate = useNavigate()
  const { user, isAdmin, adminChecked, isOwner } = useAuth()
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    // Wait for admin check to complete before fetching stats
    if (!user || !adminChecked || !isAdmin) return

    const fetchStats = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession()
        const token = sessionData?.session?.access_token

        const response = await fetch(`${BACKEND_URL}/api/admin/stats`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const data = await response.json()
          setStats(data)
        } else {
          setError('Chyba pri načítaní štatistík')
        }
      } catch (err) {
        setError('Chyba pri načítaní štatistík')
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [user, adminChecked, isAdmin])

  if (!adminChecked || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Načítavam...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <div className="bg-blue-600 text-white py-6 mb-8">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold">Admin Panel</h1>
          <p className="text-blue-100 mt-1">Správa testov a používateľov</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-gray-500 text-sm font-medium mb-1">Používatelia</div>
              <div className="text-3xl font-bold text-gray-900">{stats.total_users}</div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-gray-500 text-sm font-medium mb-1">Aktívne prístupy</div>
              <div className="text-3xl font-bold text-green-600">{stats.active_subscriptions}</div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-gray-500 text-sm font-medium mb-1">Testy</div>
              <div className="text-3xl font-bold text-blue-600">{stats.total_tests}</div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-gray-500 text-sm font-medium mb-1">Dokončené pokusy</div>
              <div className="text-3xl font-bold text-purple-600">{stats.completed_attempts}</div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Rýchle akcie</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/admin/tests')}
              className="p-4 border-2 border-blue-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition text-left"
            >
              <div className="text-lg font-semibold text-blue-600 mb-1">Spravovať testy</div>
              <div className="text-sm text-gray-600">Vytvoriť, upraviť alebo zobraziť testy</div>
            </button>

            <button
              onClick={() => navigate('/admin/financial-scenarios')}
              className="p-4 border-2 border-green-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition text-left"
            >
              <div className="text-lg font-semibold text-green-600 mb-1">Finančná gramotnosť</div>
              <div className="text-sm text-gray-600">Spravovať scenáre finančnej gramotnosti</div>
            </button>

            <button
              onClick={() => navigate('/admin/custom-offers')}
              className="p-4 border-2 border-orange-200 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition text-left"
            >
              <div className="text-lg font-semibold text-orange-600 mb-1">🎁 Custom ponuky</div>
              <div className="text-sm text-gray-600">Vytvoriť vlastné ceny pre školy</div>
            </button>

            <button
              onClick={() => navigate('/admin/payments')}
              className="p-4 border-2 border-purple-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition text-left"
            >
              <div className="text-lg font-semibold text-purple-600 mb-1">💳 Platby</div>
              <div className="text-sm text-gray-600">Prehľad platieb a fakturačné údaje</div>
            </button>

            <button
              onClick={() => navigate('/admin/users')}
              className="p-4 border-2 border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition text-left"
              data-testid="admin-action-users"
            >
              <div className="text-lg font-semibold text-slate-700 mb-1">Používatelia</div>
              <div className="text-sm text-gray-600">Prehľad registrovaných používateľov a ich prístupu</div>
            </button>

            {isOwner && (
              <button
                onClick={() => navigate('/admin/admins')}
                className="p-4 border-2 border-amber-200 rounded-lg hover:border-amber-400 hover:bg-amber-50 transition text-left"
                data-testid="admin-action-admins"
              >
                <div className="text-lg font-semibold text-amber-700 mb-1">Správa adminov</div>
                <div className="text-sm text-gray-600">Udeliť/odobrať admin práva, zmeniť heslo (iba owner)</div>
              </button>
            )}

            <button
              onClick={() => navigate('/dashboard')}
              className="p-4 border-2 border-gray-200 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition text-left"
            >
              <div className="text-lg font-semibold text-gray-600 mb-1">Späť na Dashboard</div>
              <div className="text-sm text-gray-600">Návrat do používateľského rozhrania</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard
